import React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import navigation from 'utils/navigation'
import { NotFound } from 'core/errors'
import Dashboard from 'app/dashboard/Container'

import PermissionRoute from 'utils/Subscription/PermissionRoute'
import {
  PEMRISSION_ROAD_ASSISTANCE,
  PERMISSION_ACCOUNT_READ,
  PERMISSION_ANY,
  PERMISSION_APPOINTMENT_PROPOSAL_READ,
  PERMISSION_APPOINTMENT_READ,
  PERMISSION_CONCEPT_APP,
  PERMISSION_CONCEPT_READ_TENDERS,
  PERMISSION_COURTESYVEHICLES_READ,
  PERMISSION_CRM_READ,
  PERMISSION_DASHBOARD,
  PERMISSION_EXTERNALINVOICE_READ,
  PERMISSION_HISTORY,
  PERMISSION_INTAKE_PROPOSAL_READ,
  PERMISSION_PART_READ,
  PERMISSION_RENTALVEHICLES_READ,
  PERMISSION_REPORTING_READ,
  PERMISSION_RMA_READ,
  PERMISSION_STATS_READ,
  PERMISSION_TYRE_WARRANTY,
  PERMISSION_VEHICLESALES_READ,
  PERMISSION_VEHICLE_READ,
  PERMISSION_WOM_PLUS,
  PERMISSION_WORKORDER_READ
} from 'utils/Subscription/Permissions'
import { PERMISSION_ONLY_ORDER_READ } from 'utils/Subscription/PermissionComposites'
import lazyWithReload from 'utils/lazy-with-reload'
import { OrderOpenApprovalsUrl, OrderQuotationsUrl, OrderUrl } from 'app/order/RouteConstants'
import { IsFleetLeaseSetUp } from 'app/fleet-lease/IsFleetLeaseSetUp'
import { WarrantyClaimsUrl, WomPlusUrl, WorkOrderInvoicesUrl, WorkOrderQuotationsUrl, WorkOrderUrl } from 'app/workorder/RouteConstants'
import { Loader } from '@carsys/ui'
import { Feature } from '@cbgms/base/feature-flags'
import RefurbishmentContainer from 'app/accounts/components/RefurbishmentContainer'

const RoadAssistanceSubscriptionContainer = lazyWithReload(
  () => import(/* webpackPrefetch: true */ 'app/vehicles/components/Material/RoadAssistanceSubscriptions/RoadAssistanceContainer')
)

const DashboardRoutes = lazyWithReload(() => import(/* webpackPrefetch: true */ 'routes/dashboard'))
const HistoryRoutes = lazyWithReload(() => import(/* webpackPrefetch: true */ 'routes/history'))
const SettingsRoutes = lazyWithReload(() => import(/* webpackPrefetch: true */ 'routes/settings'))
const TendersRoutes = lazyWithReload(() => import(/* webpackPrefetch: true */ 'routes/tenders/TendersRoutes'))
const AppointmentProposalRoutes = lazyWithReload(
  () => import(/* webpackPrefetch: true */ 'routes/AppointmentProposal/AppointmentProposalRoutes')
)
const SchedulerRoutes = lazyWithReload(() => import(/* webpackPrefetch: true */ 'routes/scheduler/SchedulerRoutes'))

const WeekCalendarContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/workorderAppointments/components/Container'))
const StockContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/stock/components/Container'))
const WorkorderContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/workorder/components/Container'))
const AccountsContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/accounts/components/Container'))

const ConceptAppContainer = lazyWithReload(() => import(/* webpackPrefetch true */ 'app/ConceptApp/components/ConceptAppContainer'))
const TyreWarrantyContainer = lazyWithReload(() => import(/* webpackPrefetch true */ 'app/TyreWarranty/components/TyreWarrantyContainer'))

const Contact = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/contact/Contact'))
const VehiclesContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/vehicles/routes/Container'))
const UserSettingsContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/menu/components/UserSettings/Container'))
const OrdersContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/order/components/Container'))
const OrderArchiveContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/archive/components/Container'))
const ExternalArchiveContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/archive-external/Container'))
const PurchaseInvoiceContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/purchaseInvoice/components/Container'))
const StatsContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/stats/Container'))
const ReportingContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/reporting/Container'))
const VehicleSalesContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/vehicleSales/Container'))
const CourtesyVehiclesContainer = lazyWithReload(
  () => import(/* webpackPrefetch: true */ 'app/replacementVehicles/courtesyVehicles/Container')
)
const RentalVehiclesContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/replacementVehicles/rentalVehicles/Container'))
const CRMContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/crm/Container'))
const RMAContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/rma/Container'))
const ProductWarrantyClaimContainer = lazyWithReload(
  () => import(/* webpackPrefetch: true */ 'app/productWarrantyClaim/components/Container')
)
const IntakeProposalRoutes = lazyWithReload(() => import(/* webpackPrefetch: true */ './IntakeProposal/IntakeProposalRoutes'))
const ExternalOrderHandler = lazyWithReload(
  () => import(/* webpackPrefetch: true */ 'app/workorder/components/WholesalerIframe/ExternalOrderHandler')
)
const ExternalInvoiceContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/externalInvoice/component/Container'))
const WholesalerContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/wholesaler/WholesalerContainer'))
const SupplierOrdersContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/supplierOrders/components/Container'))
const WOMPlusIFrameContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/workorder/components/WOMPlus/WOMPlusIFrame'))

import { supplierOrdersUrl } from 'app/supplierOrders/routes'

class ModulesRoutes extends React.Component {
  componentDidCatch() {
    navigation.push('/404')
  }

  public render() {
    return (
      // This loading spinner supersedes the one in ApplicationRoutes, because it renders the spinner insead of the module container.
      // So the menu bar and top bar is still shown.
      <React.Suspense fallback={<Loader loading fading={false} />}>
        <Switch>
          <Route path='/' exact component={Dashboard} />
          <Route path='/404' component={NotFound} />
          <PermissionRoute
            permission={[PERMISSION_VEHICLE_READ, PEMRISSION_ROAD_ASSISTANCE]}
            path='/vehicles/road-assistance'
            component={RoadAssistanceSubscriptionContainer}
          />
          <PermissionRoute permission={PERMISSION_ACCOUNT_READ} path='/accounts' component={AccountsContainer} />
          <PermissionRoute permission={PERMISSION_CONCEPT_APP} path='/conceptapp'>
            <Feature feature='concept-app-flag' fallback={<Redirect to='/404' />}>
              <ConceptAppContainer />
            </Feature>
          </PermissionRoute>
          <PermissionRoute permission={PERMISSION_TYRE_WARRANTY} path='/tyrewarranty'>
            <Feature feature='concept-app-tyre-warranty' fallback={<Redirect to='/404' />}>
              <TyreWarrantyContainer />
            </Feature>
          </PermissionRoute>
          <PermissionRoute permission={PERMISSION_APPOINTMENT_READ} path='/calendar' component={WeekCalendarContainer} />
          <PermissionRoute permission={PERMISSION_APPOINTMENT_READ} path='/scheduler' component={SchedulerRoutes} />
          <PermissionRoute permission={PERMISSION_DASHBOARD} path='/dashboard' component={DashboardRoutes} />
          <PermissionRoute permission={PERMISSION_ANY} path='/forms' component={RefurbishmentContainer} />
          <PermissionRoute permission={PERMISSION_HISTORY} path='/history' component={HistoryRoutes} />
          <PermissionRoute permission={PERMISSION_PART_READ} path='/stock' component={StockContainer} />
          <PermissionRoute permission={PERMISSION_VEHICLE_READ} path='/vehicles' component={VehiclesContainer} />
          <PermissionRoute permission={PERMISSION_WORKORDER_READ} path={WorkOrderUrl}>
            <IsFleetLeaseSetUp>
              <WorkorderContainer />
            </IsFleetLeaseSetUp>
          </PermissionRoute>
          <PermissionRoute permission={PERMISSION_WORKORDER_READ} path={WorkOrderQuotationsUrl} component={WorkorderContainer} />
          <PermissionRoute permission={PERMISSION_WOM_PLUS} path={WomPlusUrl} component={WOMPlusIFrameContainer} />
          <PermissionRoute permission={PERMISSION_WORKORDER_READ} path={WorkOrderInvoicesUrl} component={WorkorderContainer} />
          <PermissionRoute permission={PERMISSION_WORKORDER_READ} path={WarrantyClaimsUrl} component={WorkorderContainer} />
          <PermissionRoute permission={PERMISSION_ONLY_ORDER_READ} path={OrderUrl} component={OrdersContainer} />
          <PermissionRoute permission={PERMISSION_ONLY_ORDER_READ} path={OrderQuotationsUrl} component={OrdersContainer} />
          <PermissionRoute permission={PERMISSION_ONLY_ORDER_READ} path={OrderOpenApprovalsUrl} component={OrdersContainer} />
          <PermissionRoute permission={PERMISSION_RMA_READ} path='/returns' component={RMAContainer} />
          <Route path='/product-warranty-claims' component={ProductWarrantyClaimContainer} />
          <PermissionRoute permission={PERMISSION_CONCEPT_READ_TENDERS} path='/tenders' component={TendersRoutes} />
          <PermissionRoute permission={PERMISSION_APPOINTMENT_PROPOSAL_READ} path='/proposal' component={AppointmentProposalRoutes} />
          <PermissionRoute permission={PERMISSION_INTAKE_PROPOSAL_READ} path='/intake' component={IntakeProposalRoutes} />
          <PermissionRoute permission={PERMISSION_STATS_READ} path='/stats' component={StatsContainer} />
          <PermissionRoute permission={PERMISSION_REPORTING_READ} path='/reporting' component={ReportingContainer} />
          <PermissionRoute permission={PERMISSION_VEHICLESALES_READ} path='/vehicle-sales' component={VehicleSalesContainer} />
          <PermissionRoute permission={PERMISSION_COURTESYVEHICLES_READ} path='/courtesy-vehicles'>
            <IsFleetLeaseSetUp>
              <CourtesyVehiclesContainer />
            </IsFleetLeaseSetUp>
          </PermissionRoute>
          <PermissionRoute permission={PERMISSION_RENTALVEHICLES_READ} path='/rental-vehicles' component={RentalVehiclesContainer} />
          <PermissionRoute permission={PERMISSION_CRM_READ} path='/crm' component={CRMContainer} />
          <PermissionRoute permission={PERMISSION_EXTERNALINVOICE_READ} path='/financial' component={ExternalInvoiceContainer} />
          <Route path='/wholesaler' component={WholesalerContainer} />
          <Route path='/archive' component={OrderArchiveContainer} />
          <Route path='/archive-external' component={ExternalArchiveContainer} />
          <Route path='/purchase-invoices' component={PurchaseInvoiceContainer} />
          <Route path='/settings' component={SettingsRoutes} />
          <Route path='/user-settings' component={UserSettingsContainer} />
          <Route path={supplierOrdersUrl} component={SupplierOrdersContainer} />
          <Route exact path='/contact' component={Contact} />
          <Route exact path='/external-order-handler' component={ExternalOrderHandler} />
          <Redirect to='/404' />
        </Switch>
      </React.Suspense>
    )
  }
}

export default ModulesRoutes
